import { getError } from './ErrorHelper';
import { registerHandler, receiveMessage } from './MessageManager';

import utils = egr.wcf.utils;

export interface BaseMessage {
    type: string;
    action: string;
}

interface RawBaseMassage extends BaseMessage {
    transaction_id: number;
}

export interface Message<T> extends BaseMessage {
    arguments: T;
}

interface WKMessageHandler {
    postMessage: (message: object, target: string) => void;
}

declare global {
    interface Window {
        droid?: {
            postMessage: (message: string) => void;
        };
        webkit?: {
            messageHandlers: {
                api?: WKMessageHandler,
                fs: WKMessageHandler,
                share: WKMessageHandler,
                progress: WKMessageHandler,
                project: WKMessageHandler
            }
        };
    }
}

declare var WindowsAppMessageSender: {
    trigger: (message: string) => void;
    getVersion: () => string;
};

abstract class AppError extends utils.CustomError {
    constructor(message?: string) {
        super(message);

        Object.setPrototypeOf(this, AppError.prototype);
    }
}

interface AppErrorConstructor {
    new(message: string): AppError;
}

const AppErrors: Map<string, AppErrorConstructor> = new Map<string, AppErrorConstructor>();

class UnknownAppError extends AppError {
    constructor(message?: string) {
        super(message);

        Object.setPrototypeOf(this, UnknownAppError.prototype);
    }
}

function getAppError(message?: string): AppError {
    if (utils.string.isNotNullOrEmpty(message)) {
        const [uuid, ...msg] = message.split(':');

        const constructor: AppErrorConstructor | undefined = AppErrors.get(uuid);
        if (constructor !== undefined) {
            // Note: the javascript split function is stupid (compared to the python split function)
            // -> so we have to use a join (or a regex instead of the split function), because the
            // limit parameter does not work as expected (and therefor is not really useful at all)
            return new constructor(msg.join(':').trim());
        }
    }

    return new UnknownAppError(message);
}

const MessageTimeout: number = 10000;

interface RequestProgress {
    time: number;
    request_id: number;
}

interface PromiseMapping {
    resolve: (data: {} | null) => void;
    reject: (data: Error) => void;
    updateProgress: (data: RequestProgress) => void;
    timeout: number;
}

interface SendMessageCallbacks<RequestProgressType extends RequestProgress> {
    progress?: (data: RequestProgressType) => void;
    transactionId?: (transactionId: number) => void;
}

let TransactionIdCounter: number = 0;
const PromiseQueue: {[id: number]: PromiseMapping} = {};

type LogHandler = (msg: BaseMessage) => void;

const LogHandlers: Array<LogHandler> = [];

export function registerLogHandler(handler: LogHandler): void {
    LogHandlers.push(handler);
}

function log(msg: BaseMessage): void {
    for (const handler of LogHandlers) {
        try {
            handler(msg);
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error(error);
        }
    }
}

let SelfTalk: boolean = false;
export function enableSelfTalk(): void {
    SelfTalk = true;
}

export function sendMessage<MessageType extends BaseMessage = BaseMessage, ReplyType = void, ProgressType extends RequestProgress = RequestProgress>(
    message: MessageType,
    noReply: boolean = false,
    messageTimeout: number = MessageTimeout,
    callbacks: SendMessageCallbacks<ProgressType> | null = null
): Promise<ReplyType> {
    const rawMessage: RawBaseMassage = (message as {} as RawBaseMassage);

    if (noReply) {
        rawMessage.transaction_id = -1;
    } else {
        // Note: it is specified, that the id has to end with 0
        rawMessage.transaction_id = TransactionIdCounter++ * 10;
    }

    if (callbacks != null && callbacks.transactionId != null) {
        callbacks.transactionId(rawMessage.transaction_id);
    }

    log(rawMessage);

    return new Promise((resolve: (data: ReplyType) => void, reject: (error: Error) => void) => {
        if (noReply === false) {
            const error: Error = getError(
                'Message timeout reached!',
                {
                    tags: ['project-api'],
                    extra: {
                        message: rawMessage
                    }
                }
            );

            PromiseQueue[rawMessage.transaction_id] = {
                resolve: resolve as (data: {} | null) => void,
                reject: reject,
                timeout: setTimeout(
                    (): void => {
                        reject(error);
                    },
                    messageTimeout
                ),
                updateProgress: ((data: ProgressType): void => {
                    const mapping: PromiseMapping = PromiseQueue[rawMessage.transaction_id];
                    if (mapping != null) {
                        clearTimeout(mapping.timeout);
                        mapping.timeout = setTimeout(
                            (): void => {
                                reject(error);
                            },
                            messageTimeout
                        );

                        if (callbacks != null && callbacks.progress != null) {
                            callbacks.progress(data);
                        }
                    }
                }) as (data: RequestProgress) => void
            };
        }

        if (window.webkit != null && window.webkit.messageHandlers.api != null) {
            window.webkit.messageHandlers.api.postMessage(rawMessage, '*');
        } else if (window.droid != null && window.droid.postMessage != null) {
            window.droid.postMessage(JSON.stringify(rawMessage));
        } else if (typeof WindowsAppMessageSender !== 'undefined') {
            WindowsAppMessageSender.trigger(JSON.stringify(rawMessage));
        } else if (SelfTalk) {
            _fakeMessage(rawMessage);
        }

        if (noReply === true) {
            // Note: workaround to avoid null checks in every caller
            resolve(undefined!);
        }
    });
}

function replyMessageHandler(event: MessageEvent): void {
    log(event.data);

    if (event.data.transaction_id != null) {
        const source: PromiseMapping = PromiseQueue[event.data.transaction_id];
        if (source != null) {
            if (event.data.error === true) {
                source.reject(getAppError(event.data.data));
            } else {
                source.resolve(event.data.data);
            }

            clearTimeout(source.timeout);

            delete PromiseQueue[event.data.transaction_id];
        }
    }
}

registerHandler({
    type: 'reply',
    callback: replyMessageHandler
});

function progressMessageHandler(event: MessageEvent): void {
    log(event.data);

    if (
        event.data.action != null && event.data.action === 'request' &&
        event.data.arguments != null && event.data.arguments.request_id != null
    ) {
        const source: PromiseMapping = PromiseQueue[event.data.arguments.request_id];
        if (source != null) {
            source.updateProgress(event.data.arguments as RequestProgress);
        }
    }
}

registerHandler({
    type: 'progress',
    callback: progressMessageHandler
});

/** @internal */
export function _sendReply(
    transactionId: number,
    error: boolean,
    data: object | null | string | number | void = null
): void {
    window.postMessage(
        {
            transaction_id: transactionId,
            type: 'reply',
            error: error,
            data: data
        },
        '*'
    );
}

/** @internal */
export function _fakeMessage<T extends RawBaseMassage = RawBaseMassage>(message: T): void {
    window.postMessage(message, '*');
}

// Note: workaround needed for the universal windows platform app
(window as {WindowsAppMessageReceiver?: (data: string) => void}).WindowsAppMessageReceiver = (data: string): void => {
    const event: MessageEvent = ({
        data: JSON.parse(data)
    } as MessageEvent);

    receiveMessage(event);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/app-api/Communication.ts